/**
 * author-articles-list: Web Component that lazy-loads and displays all articles
 * by a given author via Shopify Section Rendering API (main-search section),
 * with in-place pagination and no full page reload.
 */
class AuthorArticlesList extends HTMLElement {
	static get CONTAINER_SELECTOR() {
		return '.author-articles-list__container';
	}

	static get EXTRACT_SELECTOR() {
		return '#main-search-articles-pagination';
	}

	constructor() {
		super();
		this.#loading = false;
		this.#fetched = false;
		this.#boundOnClick = this.#onClick.bind(this);
	}

	#loading;
	#fetched;
	#boundOnClick;
	#observer = null;

	connectedCallback() {
		const container = this.querySelector(AuthorArticlesList.CONTAINER_SELECTOR);
		if (!container) return;

		const author = this.dataset.author;
		if (!author || !author.trim()) return;

		this.#observer = new IntersectionObserver(
			(entries) => {
				if (!entries[0].isIntersecting) return;
				this.#observer.unobserve(this);

				const initialPage = this.#getPageFromUrl();
				this.#fetch(initialPage);
			},
			{ rootMargin: '0px 0px 100px 0px' },
		);
		this.#observer.observe(this);
		container.addEventListener('click', this.#boundOnClick);
	}

	disconnectedCallback() {
		this.#observer?.disconnect();
		const container = this.querySelector(AuthorArticlesList.CONTAINER_SELECTOR);
		if (container) container.removeEventListener('click', this.#boundOnClick);
	}

	#getContainer() {
		return this.querySelector(AuthorArticlesList.CONTAINER_SELECTOR);
	}

	#buildSearchUrl(page = 1) {
		const base = (this.dataset.searchUrl || '/search').replace(/\/$/, '');
		const sectionId = this.dataset.sectionId || 'main';
		const author = this.dataset.author.trim();
		const query = `author:${author} -title:${author}`;
		const params = new URLSearchParams({
			q: query,
			type: 'article',
			page: String(page),
			section_id: sectionId,
		});
		return `${base}?${params.toString()}`;
	}

	#setState(state) {
		const container = this.#getContainer();
		if (!container) return;

		const loading = container.querySelector('[data-state="loading"]');
		const error = container.querySelector('[data-state="error"]');
		if (loading) loading.hidden = state !== 'loading';
		if (error) error.hidden = state !== 'error';
		this.classList.toggle('author-articles-list--loading', state === 'loading');
	}

	#scrollToTop() {
		this.scrollIntoView({ behavior: 'smooth', block: 'start' });
	}

	#getPageFromUrl() {
		const url = new URL(window.location.href);
		const pageParam = url.searchParams.get('page');
		if (!pageParam) return 1;

		const pageNum = parseInt(pageParam, 10);
		if (Number.isNaN(pageNum) || pageNum < 1) return 1;

		return pageNum;
	}

	#extractContent(htmlString) {
		const doc = new DOMParser().parseFromString(htmlString, 'text/html');
		const block = doc.querySelector(AuthorArticlesList.EXTRACT_SELECTOR);
		if (!block) return '';
		const heading = block.querySelector('h2');
		if (heading) heading.remove();
		return block.innerHTML;
	}

	#bindPagination(container) {
		container.querySelectorAll('a[href*="/search"]').forEach((link) => {
			link.setAttribute('href', link.getAttribute('href') || '#');
		});
	}

	#updatePageInUrl(page) {
		const url = new URL(window.location.href);

		if (page <= 1) {
			url.searchParams.delete('page');
		} else {
			url.searchParams.set('page', String(page));
		}

		window.history.replaceState({}, '', url);
	}

	#onClick(e) {
		const link = e.target.closest('a[href*="/search"]');
		if (!link) return;
		e.preventDefault();
		const href = link.getAttribute('href');
		if (!href) return;
		const url = new URL(href, window.location.origin);
		const page = url.searchParams.get('page') || '1';
		const pageNum = parseInt(page, 10);
		if (pageNum >= 1) {
			this.#scrollToTop();
			this.#fetch(pageNum);
		}
	}

	async #fetch(page) {
		if (this.#loading) return;

		const container = this.#getContainer();
		if (!container) return;

		this.#loading = true;
		this.#setState('loading');

		try {
			const url = this.#buildSearchUrl(page);
			const response = await fetch(url, { headers: { Accept: 'text/html' } });
			const html = await response.text();
			const content = this.#extractContent(html);

			if (!content.trim()) {
				this.#setState('error');
				this.#loading = false;
				return;
			}

			let wrap = container.querySelector('.author-articles-list__content');
			if (wrap) {
				wrap.innerHTML = content;
			} else {
				wrap = document.createElement('div');
				wrap.className = 'author-articles-list__content';
				wrap.innerHTML = content;
				container.appendChild(wrap);
			}
			this.#setState('idle');
			this.#fetched = true;
			this.#bindPagination(wrap);
			this.#updatePageInUrl(page);
		} catch (err) {
			this.#setState('error');
		} finally {
			this.#loading = false;
		}
	}
}

if (typeof customElements.get('author-articles-list') === 'undefined') {
	customElements.define('author-articles-list', AuthorArticlesList);
}
